import { HeartPulse, PhoneCall } from 'lucide-react';

// Refill adherence: a pharmacy counter can't see pills being taken, so the
// honest signal is whether the patient came back for the next pack on time.
export const AdherencePanel = ({ data }) => {
  const summary = data?.summary || {};
  const patients = data?.patients || [];

  if (patients.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-muted py-16 text-center text-muted-foreground" data-testid="refill-adherence-empty">
        <HeartPulse className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm">No refill history to score yet.</p>
        <p className="text-xs opacity-70 mt-1">Adherence appears once patients have completed or missed a refill cycle.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="refill-adherence-panel">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <SummaryTile label="Patients tracked" value={summary.patients_tracked ?? 0} testid="adherence-tracked" />
        <SummaryTile label="On-time rate" value={summary.on_time_rate === null || summary.on_time_rate === undefined ? '—' : `${summary.on_time_rate}%`} testid="adherence-rate" />
        <SummaryTile label="Falling behind" value={summary.falling_behind ?? 0} tone="rose" testid="adherence-falling-behind" />
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[11px] uppercase font-semibold tracking-wide text-muted-foreground border-b bg-muted/30">
          <div className="col-span-4">Patient</div>
          <div className="col-span-3">Medications</div>
          <div className="col-span-2 text-center">On time</div>
          <div className="col-span-1 text-center">Late</div>
          <div className="col-span-2 text-right">Needs a call</div>
        </div>
        {patients.map((p) => (
          <div key={p.patient_id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b last:border-0 items-center text-sm" data-testid={`adherence-row-${p.patient_id}`}>
            <div className="col-span-4">
              <p className="font-medium">{p.patient_name}</p>
              <p className="text-xs text-muted-foreground">
                {p.completed} completed{p.open_overdue ? ` · ${p.open_overdue} overdue now` : ''}
              </p>
            </div>
            <div className="col-span-3 text-xs text-muted-foreground truncate" title={p.medications.join(', ')}>
              {p.medications.join(', ') || '—'}
            </div>
            <div className="col-span-2 text-center tabular-nums font-semibold">
              {p.on_time_rate === null ? '—' : `${p.on_time_rate}%`}
            </div>
            <div className="col-span-1 text-center tabular-nums text-muted-foreground">
              {p.late}{p.avg_days_late ? ` (${p.avg_days_late}d)` : ''}
            </div>
            <div className="col-span-2 text-right">
              {p.falling_behind && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">
                  <PhoneCall className="h-3 w-3" /> Follow up
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        A refill counts as on time when it was collected within {summary.grace_days ?? 3} days of its due date.
      </p>
    </div>
  );
};

const SummaryTile = ({ label, value, tone = 'slate', testid }) => (
  <div className="rounded-xl border bg-card p-3" data-testid={testid}>
    <p className={`text-2xl font-bold tabular-nums text-${tone}-700`}>{value}</p>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-1">{label}</p>
  </div>
);
